import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { RolePill } from "@/components/settings/RolePill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { FolderCog, Loader2, Trash2 } from "lucide-react";

export type OrgMember = {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  role: string;
  status: string;
};

/** What the actor may do to this member — derived from the org tier matrix. */
export interface MemberPermissions {
  /** Roles the actor may switch this member to; empty when the row is read-only. */
  assignableRoles: string[];
  canManageProjects: boolean;
  canRemove: boolean;
  /** Whether the actions column is rendered at all (actor manages anyone). */
  showActions: boolean;
}

export interface MemberActions {
  onRoleChange: (member: OrgMember, role: string) => void;
  onManageProjects: (member: OrgMember) => void;
  onRemove: (member: OrgMember) => void;
}

export interface UserRoleRowProps {
  member: OrgMember;
  perms: MemberPermissions;
  actions: MemberActions;
  saving: boolean;
}

/** Active / inactive, as a pill. */
function StatusCell({ status }: { status: string }) {
  const { t } = useLanguage();
  const isActive = status === "active";

  return (
    <Badge
      variant={isActive ? "default" : "secondary"}
      className={
        isActive ? "bg-green-100 font-normal text-green-800 hover:bg-green-100" : "font-normal"
      }
    >
      {isActive ? t.settings.roles.active : t.settings.roles.inactive}
    </Badge>
  );
}

/** Manage a subcontractor's projects, and remove the member from the org. */
function ActionsCell({ member, perms, actions, saving }: UserRoleRowProps) {
  const { t } = useLanguage();

  return (
    <TableCell className="text-right">
      <div className="flex items-center justify-end gap-1">
        {perms.canManageProjects && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="aria-disabled:opacity-50"
                onClick={() => {
                  if (!saving) actions.onManageProjects(member);
                }}
                aria-disabled={saving}
                aria-label={t.settings.roles.manageProjectsTitle}
              >
                <FolderCog className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t.settings.roles.manageProjectsTitle}</TooltipContent>
          </Tooltip>
        )}
        {perms.canRemove && (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => actions.onRemove(member)}
            disabled={saving}
            aria-label={t.settings.roles.removeMemberTitle}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </Button>
        )}
      </div>
    </TableCell>
  );
}

/** One org member as a flat, hairline-separated table row. */
export function UserRoleRow({ member, perms, actions, saving }: UserRoleRowProps) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell className="font-medium">
        {member.first_name} {member.last_name}
      </TableCell>
      <TableCell className="text-muted-foreground">{member.email}</TableCell>
      <TableCell>
        <RolePill
          role={member.role}
          assignableRoles={perms.assignableRoles}
          saving={saving}
          onChange={(role) => actions.onRoleChange(member, role)}
        />
      </TableCell>
      <TableCell>
        <StatusCell status={member.status} />
      </TableCell>
      {perms.showActions && (
        <ActionsCell member={member} perms={perms} actions={actions} saving={saving} />
      )}
    </TableRow>
  );
}
